import { useRef, useState } from 'react';
import './FileDrop.css';

const ACCEPT = '.pdf,.doc,.docx,.txt,.xls,.xlsx';
const MAX_BYTES = 20 * 1024 * 1024;

function size(bytes: number) {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Choose a document by dropping it here or clicking. Once chosen it shows as
 * a row with its name and size, and can be removed.
 */
export function FileDrop({ id, file, onChange, hint }: { id: string; file: File | null; onChange: (f: File | null) => void; hint?: string }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const take = (f: File | undefined | null) => {
    setError(null);
    if (!f) return;
    const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ACCEPT.split(',').includes(`.${ext}`)) return setError('That type of file can’t be read. Use a PDF, Word, text or spreadsheet file.');
    if (f.size > MAX_BYTES) return setError('That file is over 20 MB. Add a link to it instead.');
    onChange(f);
  };

  if (file) {
    return (
      <div className="filedrop-chosen">
        <span className="filedrop-icon" aria-hidden="true">
          <DocIcon />
        </span>
        <div className="filedrop-name">
          <strong>{file.name}</strong>
          <span>{size(file.size)}</span>
        </div>
        <button type="button" className="filedrop-remove" onClick={() => onChange(null)}>
          Remove
        </button>
      </div>
    );
  }

  return (
    <div className="filedrop-wrap">
      <label
        htmlFor={id}
        className="filedrop"
        data-over={over || undefined}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          take(e.dataTransfer.files?.[0]);
        }}
      >
        <span className="filedrop-icon" aria-hidden="true">
          <UploadIcon />
        </span>
        <span className="filedrop-text">
          <strong>Choose a file</strong> or drag it here
        </span>
        <span className="filedrop-hint">{hint ?? 'PDF, Word, text or spreadsheet, up to 20 MB'}</span>
      </label>
      <input
        ref={input}
        id={id}
        type="file"
        accept={ACCEPT}
        className="filedrop-input"
        onChange={(e) => {
          take(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {error ? <p className="field-message">{error}</p> : null}
    </div>
  );
}

function UploadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 16V4" />
      <path d="m7 9 5-5 5 5" />
      <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h4" />
    </svg>
  );
}
